import { useState, useContext, useEffect } from "react";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";
import { FileText, Calendar, Archive, Loader2, Sparkles, TrendingUp, Mail, Lightbulb, UserCircle, AlertCircle, CheckCircle, Download } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Alert, AlertDescription } from "@/components/ui/alert";
import ResearchPreferences from "@/components/ResearchPreferences";
import VotingButtons from "@/components/VotingButtons";
import NewsletterWithVoting from "@/components/NewsletterWithVoting";
import NewsletterChat from "@/components/NewsletterChat";
import FeedbackSection from "@/components/FeedbackSection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const roles = [
  "Digital Marketing",
  "Sales",
  "Finance",
  "Operations",
  "Claims Management",
  "Underwriting",
  "Risk Assessment",
  "Customer Service",
  "Product Development",
  "Compliance"
];

const focusAreas = [
  { id: "news", label: "Industry News & Updates", icon: TrendingUp },
  { id: "insights", label: "Strategic Insights & Analysis", icon: Lightbulb },
  { id: "ideas", label: "Out-of-the-Box Ideas", icon: Sparkles },
  { id: "competitive", label: "Competitive Intelligence", icon: FileText },
  { id: "discussions", label: "Community Discussions", icon: Mail },
  { id: "best-practices", label: "Best Practices & Case Studies", icon: Calendar }
];

const HomePage = () => {
  const navigate = useNavigate();
  const { user, subscriptionStatus, refreshSubscription } = useContext(AuthContext);
  const [role, setRole] = useState("");
  const [prompt, setPrompt] = useState("");
  const [selectedFocusAreas, setSelectedFocusAreas] = useState(["news", "insights", "ideas", "best-practices"]);
  const [loading, setLoading] = useState(false);
  const [generatedNewsletter, setGeneratedNewsletter] = useState(null);
  
  // Schedule state
  const [scheduleFrequency, setScheduleFrequency] = useState("daily");
  const [scheduleDayOfWeek, setScheduleDayOfWeek] = useState("Monday");
  const [scheduleTime, setScheduleTime] = useState("09:00");
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [colleagueEmails, setColleagueEmails] = useState("");
  
  // Research preferences state - now topic-based with include/exclude
  const [researchPreferences, setResearchPreferences] = useState({
    time_period_months: 24,
    selected_topics: [],  // Topics will be selected by user
    include_keywords: "",  // Domains/keywords to include
    exclude_keywords: ""   // Domains/keywords to exclude
  });

  const handleFocusAreaToggle = (areaId) => {
    setSelectedFocusAreas(prev => {
      if (prev.includes(areaId)) {
        return prev.filter(id => id !== areaId);
      } else {
        return [...prev, areaId];
      }
    });
  };

  const handleGenerateNewsletter = async () => {
    if (!role || !prompt || selectedFocusAreas.length === 0) {
      toast.error("Please fill in all fields");
      return;
    }

    // App is free - no subscription check needed
    setLoading(true);
    try {
      // Safely map focus areas, filtering out any invalid ones
      const focusAreaLabels = selectedFocusAreas
        .map(id => focusAreas.find(f => f.id === id))
        .filter(f => f !== undefined)
        .map(f => f.label);
      
      if (focusAreaLabels.length === 0) {
        toast.error("Please select at least one focus area");
        setLoading(false);
        return;
      }

      const response = await axios.post(`${API}/generate-newsletter`, {
        role,
        prompt,
        focus_areas: focusAreaLabels,
        research_preferences: researchPreferences
      });
      
      setGeneratedNewsletter(response.data);
      toast.success("Newsletter generated successfully!");
      await refreshSubscription();
    } catch (error) {
      console.error("Error generating newsletter:", error);
      const errorMsg = error.response?.data?.detail || "Failed to generate newsletter";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleNewsletter = async () => {
    if (!role || !prompt || selectedFocusAreas.length === 0) {
      toast.error("Please fill in Role and Topic fields above to schedule");
      return;
    }

    if (scheduleFrequency === "weekly" && !scheduleDayOfWeek) {
      toast.error("Please select a day for weekly schedule");
      return;
    }

    // Safely map focus areas
    const focusAreaLabels = selectedFocusAreas
      .map(id => focusAreas.find(f => f.id === id))
      .filter(f => f !== undefined)
      .map(f => f.label);

    // App is free - no subscription check needed
    setScheduleLoading(true);
    try {
      await axios.post(`${API}/schedules`, {
        role,
        prompt,
        focus_areas: focusAreaLabels,
        frequency: scheduleFrequency,
        day_of_week: scheduleFrequency === "weekly" ? scheduleDayOfWeek : null,
        time: scheduleTime,
        research_preferences: researchPreferences,
        colleague_emails: colleagueEmails
      });
      
      toast.success("Newsletter scheduled successfully!");
      // Don't reset role and prompt - user might want to schedule another with different frequency
      // Only reset schedule-specific fields
      setScheduleFrequency("daily");
      setScheduleDayOfWeek("Monday");
      setScheduleTime("09:00");
      setColleagueEmails("");
    } catch (error) {
      console.error("Error scheduling newsletter:", error);
      const errorMsg = error.response?.data?.detail || "Failed to schedule newsletter";
      toast.error(errorMsg);
    } finally {
      setScheduleLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200/60 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_insurecloud-news/artifacts/g99eghuw_Owl%20Ai%20animated%20Gif.gif" 
                alt="AI Newsletter Logo"
                className="h-10 w-10"
              />
              <div>
                <h1 className="text-xl font-bold text-slate-900 tracking-tight">AI Newsletter</h1>
                <p className="text-xs text-slate-500">Powered by Owl Intelligence</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                data-testid="my-account-btn"
                onClick={() => navigate('/account')}
                variant="ghost"
                className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 font-medium"
              >
                <UserCircle className="mr-2 h-4 w-4" />
                Account
              </Button>
              <Button
                data-testid="view-archive-btn"
                onClick={() => navigate('/archive')}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md"
              >
                <Archive className="mr-2 h-4 w-4" />
                Archive
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Free Access Banner */}
        <div className="mb-8 p-4 rounded-xl bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-200/60">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-100">
              <CheckCircle className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <p className="font-semibold text-slate-900">Free Access Active</p>
              <p className="text-sm text-slate-600">Unlimited newsletters with deep web research - 1-week free trial</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Form */}
          <div className="space-y-6">
            {/* Research Preferences */}
            <ResearchPreferences 
              preferences={researchPreferences}
              onChange={setResearchPreferences}
            />
            <Card className="shadow-lg border-none">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-teal-50">
                <CardTitle className="text-2xl">Generate Newsletter</CardTitle>
                <CardDescription>Customize your insurance industry insights</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 pt-6">
                {/* Role Selection */}
                <div>
                  <Label htmlFor="role" className="text-base font-semibold mb-2 block">Your Role</Label>
                  <Select data-testid="role-select" value={role} onValueChange={setRole}>
                    <SelectTrigger id="role" data-testid="role-select-trigger" className="h-12">
                      <SelectValue placeholder="Select your role" />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map((r) => (
                        <SelectItem key={r} value={r} data-testid={`role-option-${r.toLowerCase().replace(/\s+/g, '-')}`}>
                          {r}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Prompt Input */}
                <div>
                  <Label htmlFor="prompt" className="text-base font-semibold mb-2 block">Your Topic/Prompt</Label>
                  <Textarea
                    id="prompt"
                    data-testid="prompt-input"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="E.g., Latest trends in digital insurance, AI in claims processing, customer retention strategies..."
                    className="min-h-[120px] text-base"
                  />
                </div>

                {/* Focus Areas */}
                <div>
                  <Label className="text-base font-semibold mb-3 block">Focus Areas</Label>
                  <div className="grid grid-cols-1 gap-3">
                    {focusAreas.map((area) => {
                      const Icon = area.icon;
                      return (
                        <div
                          key={area.id}
                          className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors cursor-pointer"
                          onClick={() => handleFocusAreaToggle(area.id)}
                          data-testid={`focus-area-${area.id}`}
                        >
                          <Checkbox
                            id={area.id}
                            checked={selectedFocusAreas.includes(area.id)}
                            onCheckedChange={() => handleFocusAreaToggle(area.id)}
                          />
                          <Icon className="h-5 w-5 text-blue-600" />
                          <label htmlFor={area.id} className="text-base cursor-pointer flex-1">
                            {area.label}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <Button
                  data-testid="generate-btn"
                  onClick={handleGenerateNewsletter}
                  disabled={loading}
                  className="w-full h-12 text-lg font-semibold bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <FileText className="mr-2 h-5 w-5" />
                      Generate Now
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Scheduling Card */}
            <Card className="shadow-lg border-none">
              <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
                <CardTitle className="text-2xl">Schedule Newsletter</CardTitle>
                <CardDescription>Automate regular newsletter generation using the role and topic above</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 pt-6">
                <div className="space-y-4">
                  {/* Frequency */}
                  <div>
                    <Label className="text-base font-semibold mb-2 block">Frequency</Label>
                    <Select data-testid="frequency-select" value={scheduleFrequency} onValueChange={setScheduleFrequency}>
                      <SelectTrigger data-testid="frequency-select-trigger" className="h-12">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily" data-testid="frequency-daily">Daily</SelectItem>
                        <SelectItem value="weekly" data-testid="frequency-weekly">Weekly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Day of Week (for weekly) */}
                  {scheduleFrequency === "weekly" && (
                    <div>
                      <Label className="text-base font-semibold mb-2 block">Day of Week</Label>
                      <Select data-testid="day-select" value={scheduleDayOfWeek} onValueChange={setScheduleDayOfWeek}>
                        <SelectTrigger data-testid="day-select-trigger" className="h-12">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map(day => (
                            <SelectItem key={day} value={day} data-testid={`day-${day.toLowerCase()}`}>{day}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Time */}
                  <div>
                    <Label htmlFor="time" className="text-base font-semibold mb-2 block">Time (24-hour format)</Label>
                    <Input
                      id="time"
                      data-testid="time-input"
                      type="time"
                      value={scheduleTime}
                      onChange={(e) => setScheduleTime(e.target.value)}
                      className="h-12 text-base"
                    />
                  </div>

                  {/* Colleague Emails */}
                  <div>
                    <Label htmlFor="colleague-emails" className="text-base font-semibold mb-2 block flex items-center gap-2">
                      <Mail className="h-5 w-5 text-purple-600" />
                      Send to My Colleagues (Optional)
                    </Label>
                    <Input
                      id="colleague-emails"
                      data-testid="colleague-emails-input"
                      type="text"
                      placeholder="john@company.com, sarah@company.com, ..."
                      value={colleagueEmails}
                      onChange={(e) => setColleagueEmails(e.target.value)}
                      className="h-12 text-base"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Separate email addresses with commas. Max 20 recipients.
                    </p>
                  </div>
                </div>

                <Button
                  data-testid="schedule-btn"
                  onClick={handleScheduleNewsletter}
                  disabled={scheduleLoading}
                  className="w-full h-12 text-lg font-semibold bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                >
                  {scheduleLoading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Scheduling...
                    </>
                  ) : (
                    <>
                      <Calendar className="mr-2 h-5 w-5" />
                      Schedule Newsletter
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Generated Newsletter */}
          <div>
            <Card className="shadow-lg border-none sticky top-6" data-testid="newsletter-preview">
              <CardHeader className="bg-gradient-to-r from-teal-50 to-blue-50">
                <CardTitle className="text-2xl">Generated Newsletter</CardTitle>
                <CardDescription>Your personalized business intelligence insights</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                {generatedNewsletter ? (
                  <div className="space-y-4" data-testid="newsletter-content">
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm text-blue-800 font-semibold">Role: {generatedNewsletter.role}</p>
                          <p className="text-sm text-blue-700 mt-1">Topic: {generatedNewsletter.prompt}</p>
                          <p className="text-xs text-blue-600 mt-2">
                            Generated: {new Date(generatedNewsletter.timestamp).toLocaleString()}
                          </p>
                        </div>
                        <Button
                          data-testid="export-pdf-btn"
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = `${API}/newsletters/${generatedNewsletter.id}/pdf`;
                            link.target = '_blank';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            toast.success("PDF download started!");
                          }}
                          variant="outline"
                          className="border-blue-300 text-blue-700 hover:bg-blue-100"
                        >
                          <Download className="mr-2 h-4 w-4" />
                          Export PDF
                        </Button>
                      </div>
                    </div>
                    
                    <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-lg border border-purple-200">
                      <p className="text-sm font-semibold text-purple-900 mb-2">
                        💡 Rate Each Idea
                      </p>
                      <p className="text-xs text-purple-700">
                        Vote on individual ideas to help us understand what's most valuable to you
                      </p>
                    </div>
                    
                    <NewsletterWithVoting 
                      newsletter={generatedNewsletter}
                      user={user}
                    />
                    
                    {/* AI Chat Feature */}
                    <NewsletterChat
                      newsletterContent={generatedNewsletter.content}
                      newsletterTitle={generatedNewsletter.prompt}
                    />
                  </div>
                ) : (
                  <div className="text-center py-20 text-gray-400">
                    <FileText className="h-20 w-20 mx-auto mb-4 opacity-30" />
                    <p className="text-lg">Your generated newsletter will appear here</p>
                    <p className="text-sm mt-2">Fill in the form and click "Generate Now"</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Feedback Section - Always visible at bottom */}
        <FeedbackSection page="homepage" />
      </div>
    </div>
  );
};

export default HomePage;